import { HttpErrorResponse } from '@angular/common/http';

import { Provider } from '../api-clients/oem-connection';

/**
 * Cross-vendor linkage exclusivity (SRA003-473).
 *
 * A Harvest Mate farm or block may be linked to at most one OEM provider at a
 * time. The BE marks each pickable HM farm/block (on `GET /farms` and
 * `GET /farms/{id}/blocks`) with `linked_provider` — the provider currently
 * claiming that target, or null when it's free.
 *
 * Given the provider already claiming a target (`linkedProvider`) and the
 * provider whose linker is being rendered (`ownProvider`), return the *other*
 * provider when the target is cross-claimed — the signal to disable that picker
 * entry — or null when the target is free, claimed by this same provider, or
 * carries an unrecognised value (defensive: an unknown string never disables).
 */
export function crossClaimedBy(
  linkedProvider: unknown,
  ownProvider: Provider,
): Provider | null {
  if (linkedProvider !== Provider.Deere && linkedProvider !== Provider.Cnh) {
    return null;
  }
  return linkedProvider === ownProvider ? null : linkedProvider;
}

/**
 * Extract the claiming provider from a `409 cross_provider_link_conflict` body
 * if the BE includes it (`linked_provider` or `provider`), so the race toast
 * can name the vendor. Returns null when absent/unrecognised — the caller then
 * falls back to a generic "another OEM provider" phrasing.
 */
export function claimingProviderFromError(
  err: HttpErrorResponse,
): Provider | null {
  const body = err?.error;
  if (!body || typeof body !== 'object') return null;
  const claimed = body.linked_provider ?? body.provider;
  return claimed === Provider.Deere || claimed === Provider.Cnh
    ? claimed
    : null;
}
